import SmartphoneIcon from "@mui/icons-material/Smartphone";
import Box from "@mui/material/Box";
import type React from "react";

import StatusLight from "@/components/StatusLight";
import { zinc } from "@/ui/tokens";
import {
  HANDS_FREE_DISPLAYS,
  type HandsFreeAvailabilityDisplay,
  NEVER_LOGGED_IN_DISPLAY,
  OFFLINE_DISPLAY,
} from "./handsFreeAvailability";

/** Present tense on purpose: the backend reports the mode the device is registered in now, not ever. */
export const PERSONAL_DEVICE_TOOLTIP = "Driver is on a personal device";

type LegendEntry = {
  swatch: React.ReactNode;
  label: string;
  description: string;
};

function handsFreeEntry(display: HandsFreeAvailabilityDisplay): LegendEntry {
  return {
    swatch: <StatusLight color={display.color} size={10} />,
    label: display.label,
    description: display.tooltip,
  };
}

/**
 * Groups mirror the filter dropdown so the two read as one vocabulary — including Offline and
 * Never logged in, which are now labelled cells rather than blanks.
 */
const LEGEND_GROUPS: { heading: string; entries: LegendEntry[] }[] = [
  {
    heading: "Work device",
    entries: [
      handsFreeEntry(HANDS_FREE_DISPLAYS.available),
      handsFreeEntry(HANDS_FREE_DISPLAYS.handsFreeOn),
      handsFreeEntry(HANDS_FREE_DISPLAYS.handsFreeOff),
      handsFreeEntry(HANDS_FREE_DISPLAYS.deviceMuted),
      handsFreeEntry(OFFLINE_DISPLAY),
    ],
  },
  {
    heading: "Personal device",
    entries: [
      {
        swatch: <SmartphoneIcon sx={{ fontSize: 14 }} />,
        label: "Personal device",
        description: PERSONAL_DEVICE_TOOLTIP,
      },
    ],
  },
  {
    heading: "Not activated",
    entries: [handsFreeEntry(NEVER_LOGGED_IN_DISPLAY)],
  },
];

/** Wide enough for the longest label ("Never logged in") so descriptions line up and labels never wrap. */
const LABEL_COLUMN_WIDTH_PX = 128;

/** Decodes the column's dots and glyphs; surfaced by hovering the column title. */
export default function DriverAvailabilityLegend() {
  return (
    <Box sx={{ display: "grid", gap: 1, lineHeight: 1.5 }}>
      {LEGEND_GROUPS.map((group) => (
        <Box key={group.heading}>
          <Box sx={{ fontSize: "0.6875rem", fontWeight: 600, color: zinc[200], mb: 0.5 }}>{group.heading}</Box>
          <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 0.5 }}>
            {group.entries.map((entry) => (
              <Box component="li" key={entry.label} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    width: LABEL_COLUMN_WIDTH_PX,
                    flexShrink: 0,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    color: zinc[200],
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "center", width: 14, flexShrink: 0 }}>{entry.swatch}</Box>
                  {entry.label}
                </Box>
                <Box component="span" sx={{ color: zinc[200] }}>
                  {entry.description}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
